/**
 * Pure view helpers for the Token Usage tab — the day×source-project pivot the chart
 * consumes, series colour assignment, and the pricing-stat formatting. Kept out of the
 * components so they unit-test under the `node` vitest environment.
 */
import type {
  UsageSourceProjectDayBucket,
  UsageSessionPricingStats,
} from '@/services/adminApi';

/**
 * Categorical series palette. Validated with the dataviz six-check validator against
 * BOTH the light and dark chart surfaces — all five slots pass the lightness band,
 * chroma floor, CVD adjacent-pair separation, normal-vision floor, and 3:1 contrast, so
 * one palette serves both modes rather than needing a flipped dark variant.
 *
 * Slots are assigned by source-project IDENTITY (the canonical order below), never by
 * rank or by position in whatever subset a range happens to contain — changing the date
 * range must not repaint the surviving series.
 */
export const SERIES_PALETTE = [
  '#00A08A',
  '#6E56CF',
  '#B85C10',
  '#2B7FD4',
  '#C2427A',
] as const;

/** Residual series for projects beyond the palette. Neutral by design, not a hue. */
export const OTHER_SERIES_COLOR = '#8A8F98';
export const OTHER_SERIES_KEY = 'Other';

/**
 * Source projects in fixed colour order. A project's colour is its index here, so a
 * project keeps the same colour whether or not the others appear in the window.
 *
 * **This list must never exceed `SERIES_PALETTE.length`** — an entry past the end would
 * index the palette out of bounds. `assignSeriesColors` folds the overflow rather than
 * emitting an undefined colour, but the ordering is the real control: anything NOT
 * listed competes for leftover slots *alphabetically*, which is how `odi` — 19% of
 * org_scion's spend in the 2026-07-28 verification — ended up anonymised in "Other"
 * while `path` and `acs` held reserved slots and emitted nothing at all.
 *
 * So these five are the projects measured to actually emit usage, in rough order of
 * spend. To add one, displace an entry that no longer emits rather than appending, or
 * extend the palette first — and the palette's five hues were validated for colourblind
 * separation in both light and dark, so extending it is a real design task, not a
 * matter of picking another hex.
 */
export const CANONICAL_SOURCE_PROJECTS = [
  'm365dotnet',
  'odi',
  'salestrainer',
  'car-inference',
  'studio',
] as const;

export interface SourceProjectSeries {
  /** Series keys in stacking + legend order. `Other`, when present, is always last. */
  keys: string[];
  colors: Record<string, string>;
  /** Range-wide total per series key. */
  totals: Record<string, number>;
  /** One row per UTC day in the window, zero-filled, ascending. */
  rows: Array<Record<string, string | number>>;
  totalTokens: number;
  /** Projects rolled into the neutral `Other` series, so the UI can still name them. */
  foldedIntoOther: string[];
}

const MS_PER_DAY = 86_400_000;

/**
 * Assign each source project a stable colour slot, folding any project past the palette
 * into a single neutral `Other` series (the dataviz rule: never cycle or generate hues).
 * Per-project numbers stay visible in the totals list beside the chart, so folding costs
 * no information.
 */
export function assignSeriesColors(projects: string[]): {
  keys: string[];
  colors: Record<string, string>;
  foldedIntoOther: string[];
} {
  const canonicalIndex = (p: string) => CANONICAL_SOURCE_PROJECTS.indexOf(p as never);

  // Known projects first, in canonical order; the rest alphabetically after them.
  const ordered = [...new Set(projects)].sort((a, b) => {
    const ia = canonicalIndex(a);
    const ib = canonicalIndex(b);
    if (ia !== -1 && ib !== -1) return ia - ib;
    if (ia !== -1) return -1;
    if (ib !== -1) return 1;
    return a.localeCompare(b);
  });

  const keys: string[] = [];
  const colors: Record<string, string> = {};
  const foldedIntoOther: string[] = [];

  for (const project of ordered) {
    const slot = canonicalIndex(project);
    // `slot < SERIES_PALETTE.length` is a guard, not a formality: indexing the palette
    // by canonical position emits `undefined` for any entry past its end, which renders
    // a colourless series instead of folding it. Falling through to the overflow path
    // below keeps the invariant self-correcting if the canonical list ever outgrows the
    // palette.
    if (slot !== -1 && slot < SERIES_PALETTE.length) {
      keys.push(project);
      colors[project] = SERIES_PALETTE[slot];
      continue;
    }
    // Unknown project: take the next unused palette slot, else fold into Other.
    const used = new Set(Object.values(colors));
    const free = SERIES_PALETTE.find(c => !used.has(c));
    if (free) {
      keys.push(project);
      colors[project] = free;
    } else {
      foldedIntoOther.push(project);
    }
  }

  if (foldedIntoOther.length > 0) {
    keys.push(OTHER_SERIES_KEY);
    colors[OTHER_SERIES_KEY] = OTHER_SERIES_COLOR;
  }

  return { keys, colors, foldedIntoOther };
}

function eachUtcDay(fromUtc: string, toUtcExclusive: string): string[] {
  const from = Date.parse(fromUtc);
  const to = Date.parse(toUtcExclusive);
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return [];
  const days: string[] = [];
  // Normalise to midnight so a mid-day range boundary still yields whole day keys.
  let cursor = Date.parse(`${new Date(from).toISOString().slice(0, 10)}T00:00:00Z`);
  while (cursor < to) {
    days.push(new Date(cursor).toISOString().slice(0, 10));
    cursor += MS_PER_DAY;
  }
  return days;
}

/**
 * Pivot the flat day×project buckets into one zero-filled row per UTC day.
 *
 * Zero-filling matters for honesty: a gap in the bars must read as "no spend that day",
 * which it only does when every day in the window is present. Days the server returned
 * no bucket for are genuinely zero — the data-begins note covers the separate case of a
 * window reaching back past retention.
 */
export function buildSourceProjectSeries(
  buckets: UsageSourceProjectDayBucket[],
  fromUtc: string,
  toUtcExclusive: string
): SourceProjectSeries {
  const { keys, colors, foldedIntoOther } = assignSeriesColors(
    buckets.map(b => b.sourceProject)
  );
  const folded = new Set(foldedIntoOther);
  const seriesKeyFor = (project: string) =>
    folded.has(project) ? OTHER_SERIES_KEY : project;

  const totals: Record<string, number> = Object.fromEntries(keys.map(k => [k, 0]));
  const byDay = new Map<string, Record<string, number>>();

  for (const bucket of buckets) {
    const key = seriesKeyFor(bucket.sourceProject);
    totals[key] = (totals[key] ?? 0) + bucket.totalTokens;
    const day = byDay.get(bucket.dateUtc) ?? {};
    day[key] = (day[key] ?? 0) + bucket.totalTokens;
    byDay.set(bucket.dateUtc, day);
  }

  const days = eachUtcDay(fromUtc, toUtcExclusive);
  const rows = days.map(dateUtc => {
    const row: Record<string, string | number> = { dateUtc };
    const values = byDay.get(dateUtc);
    for (const key of keys) row[key] = values?.[key] ?? 0;
    return row;
  });

  return {
    keys,
    colors,
    totals,
    rows,
    totalTokens: Object.values(totals).reduce((t, v) => t + v, 0),
    foldedIntoOther,
  };
}

/** Exact token count with thousands separators — the tables show precise numbers. */
export function formatExactTokens(value: number): string {
  return value.toLocaleString('en-US');
}

/**
 * Tokens-per-hour for display. Null means the rate genuinely does not exist for this
 * population (no session in range carried a positive duration) and must render as a
 * stated absence, never as zero.
 */
export function formatTokensPerHour(value: number | null): string | null {
  if (value === null || !Number.isFinite(value)) return null;
  return `${Math.round(value).toLocaleString('en-US')} tokens/hr`;
}

export interface SessionCoverageNote {
  /** True when some sessions in range carry no usable duration. */
  hasSessionsWithoutDuration: boolean;
  text: string | null;
}

/**
 * The per-hour rate is computed over duration-bearing sessions only. When any session
 * lacks a duration the UI must say so beside the rate, or the number reads as covering
 * every session in the window.
 */
export function describeSessionCoverage(
  stats: UsageSessionPricingStats
): SessionCoverageNote {
  if (stats.sessionsWithoutDurationCount <= 0) {
    return { hasSessionsWithoutDuration: false, text: null };
  }
  return {
    hasSessionsWithoutDuration: true,
    text:
      `Excludes ${stats.sessionsWithoutDurationCount.toLocaleString('en-US')} session(s) ` +
      `carrying ${formatExactTokens(stats.tokensInSessionsWithoutDuration)} tokens with no recorded duration — ` +
      'the per-hour rate covers duration-bearing sessions only.',
  };
}
